// Presentational form fields for the broadcast composer. Shared by both
// `BroadcastComposerForm` (create) and `BroadcastEditForm` (update). All
// state is owned by the parent and threaded through controlled props; this
// component is the single visual surface for "what does a broadcast look
// like before sending."
//
// Fields (top → bottom):
//   1. Channel (platform only) — Email or Direct message
//   2. Email: Subject + preheader, body (TipTap WYSIWYG), reply-to
//      Direct message: plain-text message with an "Insert first name" token
//   3. Audience (picker — platform only)
//   4. Preview button (email only — opens BroadcastPreviewIframe)
//
// Scheduling lives in the parent's action row via `ScheduleSendButton`.

import { useLayoutEffect, useRef, useState } from 'react';
import type { components } from '@leanin/api-client';
import { useTranslations } from 'use-intl';
import { COMMUNITY_ROLES, type CommunityRole } from '@leanin/shared/community-roles';
import {
  Body,
  Button,
  Caption,
  Card,
  Checkbox,
  Eyebrow,
  Field,
  FilterShelf,
  Input,
  Label,
  MultiCombobox,
  PanelHeader,
  Row,
  Select,
  Stack,
  Textarea,
} from '@leanin/ui';

import type { BroadcastSendVia } from '@leanin/shared/broadcast-enums';
import { mergeTagToken } from '@leanin/shared/email/merge-tags';
import { MESSAGE_BODY_MAX_LENGTH } from '@leanin/shared/views';

import { BroadcastAudiencePicker } from '@/components/admin/BroadcastAudiencePicker';
import { COUNTRY_OPTIONS } from '@/components/CountryPicker';
import { useAdminBroadcastAudience, type AdminAudienceTargets } from '@/lib/queries/admin';
import { usePreviewBroadcast } from '@/lib/mutations/admin';
import { usePreviewGroupBroadcast } from '@/lib/mutations/groups';
import { useErrorMessage } from '@/lib/error-message';
import type { BroadcastDoc, BroadcastPreview } from '@/lib/queries/broadcasts';
import { BroadcastBodyEditor } from './BroadcastBodyEditor';
import { BroadcastPreviewIframe } from './BroadcastPreviewIframe';
import {
  BROADCAST_CHANNELS,
  insertAtSelection,
  sendViaForChannel,
  type BroadcastChannel,
  type BroadcastContentDraft,
  type EmailContentDraft,
} from './broadcast-content-draft';

// ──────────────────────────────────────────────────────────────────────────
// Public types
// ──────────────────────────────────────────────────────────────────────────

export type BroadcastAudienceContext =
  | { readonly mode: 'platform' }
  | {
      readonly mode: 'group';
      readonly kind: 'circle' | 'network' | 'space';
      readonly groupId: string;
      readonly groupName: string;
    };

export type PlatformAudienceKind = 'filter' | 'members' | 'circles' | 'networks';

// Canonical API contract — the server's Zod schema, surfaced through
// openapi-types. Both create and update mutations expect this exact shape.
export type AudienceSpec = components['schemas']['AudienceSpec'];

// The `filter` kind's facets. Every facet is optional (empty = no
// narrowing); `country` carries its own include-unknown toggle so the toggle
// cannot exist without a country (mirrors the server's spec shape).
export interface AudienceFilterState {
  readonly communityRoles: readonly CommunityRole[];
  readonly country: { readonly code: string; readonly includeUnknown: boolean } | null;
  readonly recentActiveDays: number | null;
}

export interface AudienceState {
  readonly kind: PlatformAudienceKind;
  readonly filter: AudienceFilterState;
  readonly circles: ReadonlyArray<{ id: string; name: string; kind: 'circle' }>;
  readonly networks: ReadonlyArray<{ id: string; name: string; kind: 'network' }>;
  readonly members: ReadonlyArray<{ id: string; displayName: string; email: string }>;
}

export const EMPTY_AUDIENCE_STATE: AudienceState = {
  kind: 'filter',
  filter: { communityRoles: [], country: null, recentActiveDays: null },
  circles: [],
  networks: [],
  members: [],
};

export interface BroadcastEditorFieldsProps {
  readonly audience: BroadcastAudienceContext;
  /** Group audiences always carry `channel: 'email'`; the Channel control
   *  renders for platform audiences only. */
  readonly content: BroadcastContentDraft;
  readonly onContentChange: (content: BroadcastContentDraft) => void;
  readonly audienceState: AudienceState;
  readonly onAudienceStateChange: (s: AudienceState) => void;
  /** Existing broadcast id — required for preview, since the server's
   *  `POST /broadcasts/{id}/preview` is row-derived (in-memory overrides
   *  layered on top of the stored row). For create-mode where the row
   *  doesn't exist yet, the Preview button is hidden. */
  readonly broadcastIdForPreview?: string;
  readonly disabled?: boolean;
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers — audience-spec derivation
// ──────────────────────────────────────────────────────────────────────────

const PLATFORM_AUDIENCE_KINDS: readonly PlatformAudienceKind[] = [
  'filter',
  'members',
  'circles',
  'networks',
];

const RECENT_ACTIVE_DAY_OPTIONS = [7, 30, 90, 180, 365] as const;

export function audienceSpecFromState(state: AudienceState): AudienceSpec {
  switch (state.kind) {
    case 'filter': {
      const { communityRoles, country, recentActiveDays } = state.filter;
      return {
        kind: 'filter',
        ...(communityRoles.length > 0 ? { communityRoles: [...communityRoles] } : {}),
        ...(country !== null
          ? { country: { codes: [country.code], includeUnknown: country.includeUnknown } }
          : {}),
        ...(recentActiveDays !== null ? { recentActiveDays } : {}),
      };
    }
    case 'circles':
      return { kind: 'circles', circleIds: state.circles.map((c) => c.id) };
    case 'networks':
      return { kind: 'networks', networkIds: state.networks.map((n) => n.id) };
    case 'members':
      return { kind: 'members', userIds: state.members.map((m) => m.id) };
  }
}

// Seeds the editor from a stored spec. A stored hand-picked spec carries ids
// only; `targets` supplies their display rows (fetched by id), and an id
// with no row — an archived group, a deactivated member — drops out. A
// `spaces` spec is group-scoped, never edited here, and maps to the empty
// state.
export function audienceStateFromSpec(
  spec: AudienceSpec,
  targets: AdminAudienceTargets,
): AudienceState {
  switch (spec.kind) {
    case 'filter': {
      const code = spec.country?.codes[0];
      return {
        ...EMPTY_AUDIENCE_STATE,
        filter: {
          communityRoles: spec.communityRoles ?? [],
          country:
            spec.country !== undefined && code !== undefined
              ? { code, includeUnknown: spec.country.includeUnknown }
              : null,
          recentActiveDays: spec.recentActiveDays ?? null,
        },
      };
    }
    case 'circles':
      return { ...EMPTY_AUDIENCE_STATE, kind: 'circles', circles: targets.circles };
    case 'networks':
      return { ...EMPTY_AUDIENCE_STATE, kind: 'networks', networks: targets.networks };
    case 'members':
      return { ...EMPTY_AUDIENCE_STATE, kind: 'members', members: targets.members };
    case 'spaces':
      return EMPTY_AUDIENCE_STATE;
  }
}

// The edit form's PATCH carries `audienceSpec` only once the admin has
// changed the audience (any change yields a new state object), so saving an
// edit to the subject or body never rewrites the stored audience — not even
// when an id dropped out of the hydrated chips.
export function audiencePatch(
  initial: AudienceState,
  current: AudienceState,
): { readonly audienceSpec?: AudienceSpec } {
  return current === initial ? {} : { audienceSpec: audienceSpecFromState(current) };
}

// A hand-picked kind with nothing picked is not a sendable spec yet (the
// server requires a non-empty id list), so there is nothing to count.
function hasRecipientSelection(state: AudienceState): boolean {
  switch (state.kind) {
    case 'filter':
      return true;
    case 'circles':
      return state.circles.length > 0;
    case 'networks':
      return state.networks.length > 0;
    case 'members':
      return state.members.length > 0;
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

export function BroadcastEditorFields({
  audience,
  content,
  onContentChange,
  audienceState,
  onAudienceStateChange,
  broadcastIdForPreview,
  disabled,
}: BroadcastEditorFieldsProps) {
  const [preview, setPreview] = useState<BroadcastPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const describeError = useErrorMessage();
  const tForm = useTranslations('Admin.broadcasts.form');
  const platformPreview = usePreviewBroadcast();
  const groupPreview = usePreviewGroupBroadcast();
  const previewPending =
    platformPreview.isPending || groupPreview.isPending;
  const { email } = content;

  const handlePreview = async () => {
    if (broadcastIdForPreview === undefined) return;
    setPreviewError(null);
    try {
      const previewArgs = {
        bodyDoc: email.bodyDoc,
        subject: email.subject,
        preheaderText: email.preheaderText.length > 0 ? email.preheaderText : null,
      } as const;
      const result =
        audience.mode === 'platform'
          ? await platformPreview.mutateAsync({
              id: broadcastIdForPreview,
              ...previewArgs,
            })
          : await groupPreview.mutateAsync({
              kind: audience.kind,
              groupId: audience.groupId,
              broadcastId: broadcastIdForPreview,
              ...previewArgs,
            });
      setPreview(result);
    } catch (err) {
      setPreviewError(describeError(err, tForm('previewFailed')));
    }
  };

  return (
    <>
      <Stack gap="lg">
        {audience.mode === 'platform' ? (
          <ChannelPicker
            channel={content.channel}
            onChange={(channel) => onContentChange({ ...content, channel })}
            disabled={disabled === true}
          />
        ) : null}

        {content.channel === 'email' ? (
          <EmailContentFields
            email={email}
            onChange={(next) => onContentChange({ ...content, email: next })}
            disabled={disabled === true}
          />
        ) : (
          <DirectMessageFields
            bodyText={content.dm.bodyText}
            onChange={(bodyText) => onContentChange({ ...content, dm: { bodyText } })}
            disabled={disabled === true}
          />
        )}

        {/* Audience */}
        <Card pad="flush">
          <PanelHeader>
            <Eyebrow tone="accent">Audience</Eyebrow>
          </PanelHeader>
          <Stack p="xl" gap="lg">
            {audience.mode === 'group' ? (
              <Body size="sm" color="mute">
                Sending to all members of{' '}
                <strong>{audience.groupName}</strong>. This cannot be
                changed.
              </Body>
            ) : (
              <PlatformAudiencePicker
                state={audienceState}
                onChange={onAudienceStateChange}
                sendVia={sendViaForChannel(content.channel)}
                disabled={disabled === true}
              />
            )}
          </Stack>
        </Card>

        {/* Preview row — email only; a Direct message is its own preview. */}
        {broadcastIdForPreview !== undefined && content.channel === 'email' ? (
          <Row gap="md" align="center" justify="flex-start">
            <Button
              type="button"
              variant="secondary"
              size="md"
              disabled={disabled}
              loading={previewPending}
              onClick={() => void handlePreview()}
            >
              Preview email
            </Button>
            {previewError !== null ? (
              <Body size="sm" color="destructive">
                {previewError}
              </Body>
            ) : null}
          </Row>
        ) : null}
      </Stack>

      {preview !== null && preview.channel === 'email' ? (
        <BroadcastPreviewIframe
          html={preview.html}
          open
          onClose={() => setPreview(null)}
        />
      ) : null}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Channel + per-channel content fields (private)
// ──────────────────────────────────────────────────────────────────────────

interface ChannelPickerProps {
  readonly channel: BroadcastChannel;
  readonly onChange: (channel: BroadcastChannel) => void;
  readonly disabled: boolean;
}

function ChannelPicker({ channel, onChange, disabled }: ChannelPickerProps) {
  const t = useTranslations('Admin.broadcasts');
  return (
    <Card pad="flush">
      <PanelHeader>
        <Eyebrow tone="accent">{t('channel.label')}</Eyebrow>
      </PanelHeader>
      <Stack p="xl" gap="lg">
        <Field {...(channel === 'dm' ? { description: t('channel.dmNotice') } : {})}>
          <Select
            name="channel"
            aria-label={t('channel.label')}
            value={channel}
            onChange={(e) => onChange(e.target.value === 'dm' ? 'dm' : 'email')}
            options={BROADCAST_CHANNELS.map((c) => ({ value: c, label: t(`sendVia.${c}`) }))}
            disabled={disabled}
          />
        </Field>
      </Stack>
    </Card>
  );
}

interface EmailContentFieldsProps {
  readonly email: EmailContentDraft;
  readonly onChange: (email: EmailContentDraft) => void;
  readonly disabled: boolean;
}

function EmailContentFields({ email, onChange, disabled }: EmailContentFieldsProps) {
  return (
    <>
      {/* Subject + Preheader */}
      <Card pad="flush">
        <PanelHeader>
          <Eyebrow tone="accent">Subject &amp; preview</Eyebrow>
        </PanelHeader>
        <Stack p="xl" gap="lg">
          <Field label="Subject" required>
            <Input
              name="subject"
              value={email.subject}
              onChange={(e) => onChange({ ...email, subject: e.target.value })}
              maxLength={200}
              placeholder="A short, descriptive subject"
              disabled={disabled}
            />
          </Field>
          <Field
            label="Preview text"
            description="The inbox preview line (~120 characters)."
          >
            <Input
              name="preheaderText"
              value={email.preheaderText}
              onChange={(e) => onChange({ ...email, preheaderText: e.target.value })}
              maxLength={200}
              placeholder="What members see in their inbox before opening"
              disabled={disabled}
            />
          </Field>
        </Stack>
      </Card>

      {/* Body */}
      <Card pad="flush">
        <PanelHeader>
          <Eyebrow tone="accent">Message</Eyebrow>
        </PanelHeader>
        <Stack p="xl" gap="lg">
          <Field label="Body" required>
            <BroadcastBodyEditor
              value={email.bodyDoc}
              onChange={(bodyDoc: BroadcastDoc) => onChange({ ...email, bodyDoc })}
              disabled={disabled}
            />
          </Field>
          <Field
            label="Reply-to (optional)"
            description="Members who reply land in this inbox instead of the default."
          >
            <Input
              type="email"
              name="replyTo"
              value={email.replyTo}
              onChange={(e) => onChange({ ...email, replyTo: e.target.value })}
              placeholder="hello@leanin.org"
              disabled={disabled}
            />
          </Field>
        </Stack>
      </Card>
    </>
  );
}

interface DirectMessageFieldsProps {
  readonly bodyText: string;
  readonly onChange: (bodyText: string) => void;
  readonly disabled: boolean;
}

// Plain text — the member reads it in a chat bubble that keeps newlines.
// "Insert first name" drops the merge token at the caret (replacing any
// selection) and puts the caret after it.
function DirectMessageFields({ bodyText, onChange, disabled }: DirectMessageFieldsProps) {
  const t = useTranslations('Admin.broadcasts');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    const caret = pendingCaret.current;
    if (el === null || caret === null) return;
    pendingCaret.current = null;
    el.focus();
    el.setSelectionRange(caret, caret);
  }, [bodyText]);

  const insertFirstName = () => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? bodyText.length;
    const end = el?.selectionEnd ?? bodyText.length;
    const next = insertAtSelection(bodyText, mergeTagToken('first_name'), start, end);
    if (next.text.length > MESSAGE_BODY_MAX_LENGTH) return;
    pendingCaret.current = next.caret;
    onChange(next.text);
  };

  return (
    <Card pad="flush">
      <PanelHeader>
        <Eyebrow tone="accent">{t('sendVia.dm')}</Eyebrow>
      </PanelHeader>
      <Stack p="xl" gap="md">
        <Field label={t('dm.message')} required>
          <Textarea
            ref={textareaRef}
            name="bodyText"
            value={bodyText}
            onChange={(e) => onChange(e.target.value)}
            rows={8}
            maxLength={MESSAGE_BODY_MAX_LENGTH}
            disabled={disabled}
          />
        </Field>
        <Row gap="md" align="center" justify="space-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={insertFirstName}
            disabled={disabled}
          >
            {t('dm.insertFirstName')}
          </Button>
          <Caption>
            {t('dm.characterCount', { count: bodyText.length, max: MESSAGE_BODY_MAX_LENGTH })}
          </Caption>
        </Row>
      </Stack>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Platform-mode audience picker (private)
// ──────────────────────────────────────────────────────────────────────────

interface PlatformAudienceProps {
  readonly state: AudienceState;
  readonly onChange: (s: AudienceState) => void;
  // The channel the count is for — each channel has its own recipient gate.
  readonly sendVia: BroadcastSendVia;
  readonly disabled: boolean;
}

function PlatformAudiencePicker({
  state,
  onChange,
  sendVia,
  disabled,
}: PlatformAudienceProps) {
  const t = useTranslations('Admin.broadcasts.audience');
  const setFilter = (filter: AudienceFilterState) => onChange({ ...state, filter });

  return (
    <Stack gap="lg">
      <FilterShelf<PlatformAudienceKind>
        search={false}
        tabs={PLATFORM_AUDIENCE_KINDS.map((kind) => ({ id: kind, label: t(`kind.${kind}`) }))}
        value={state.kind}
        onChange={(kind) => onChange({ ...state, kind })}
        tabOverflow="scroll"
        tabActions={
          state.kind === 'filter'
            ? [
                {
                  id: 'facets',
                  inline: (
                    <AudienceFilterFacets
                      filter={state.filter}
                      onChange={setFilter}
                      disabled={disabled}
                    />
                  ),
                },
              ]
            : undefined
        }
      />

      {state.kind === 'circles' ? (
        <BroadcastAudiencePicker
          kind="circles"
          selectedIds={state.circles.map((c) => c.id)}
          selectedItems={state.circles}
          onChange={(_ids, items) => onChange({ ...state, circles: items })}
        />
      ) : null}

      {state.kind === 'networks' ? (
        <BroadcastAudiencePicker
          kind="networks"
          selectedIds={state.networks.map((n) => n.id)}
          selectedItems={state.networks}
          onChange={(_ids, items) => onChange({ ...state, networks: items })}
        />
      ) : null}

      {state.kind === 'members' ? (
        <BroadcastAudiencePicker
          kind="members"
          selectedIds={state.members.map((m) => m.id)}
          selectedItems={state.members}
          onChange={(_ids, items) => onChange({ ...state, members: items })}
        />
      ) : null}

      <AudienceRecipientCount
        state={state}
        sendVia={sendVia}
        onFilterChange={setFilter}
        disabled={disabled}
      />
    </Stack>
  );
}

interface AudienceFilterFacetsProps {
  readonly filter: AudienceFilterState;
  readonly onChange: (f: AudienceFilterState) => void;
  readonly disabled: boolean;
}

function AudienceFilterFacets({ filter, onChange, disabled }: AudienceFilterFacetsProps) {
  const t = useTranslations('Admin.broadcasts.audience');
  const roleOptions = COMMUNITY_ROLES.map((role) => ({ value: role, label: t(`roles.${role}`) }));

  return (
    <Row gap="md" align="flex-end" wrap>
      <Field label={t('role')}>
        <MultiCombobox<CommunityRole>
          options={roleOptions}
          value={filter.communityRoles}
          onChange={(communityRoles) => onChange({ ...filter, communityRoles })}
          placeholder={t('roleAny')}
          ariaLabel={t('role')}
          disabled={disabled}
        />
      </Field>
      <Field label={t('country')}>
        <Select
          value={filter.country?.code ?? ''}
          onChange={(e) =>
            onChange({
              ...filter,
              country:
                e.target.value === ''
                  ? null
                  : { code: e.target.value, includeUnknown: filter.country?.includeUnknown ?? false },
            })
          }
          options={[{ value: '', label: t('countryAny') }, ...COUNTRY_OPTIONS]}
          disabled={disabled}
        />
      </Field>
      <Field label={t('recentlyActive')}>
        <Select
          value={filter.recentActiveDays === null ? '' : String(filter.recentActiveDays)}
          onChange={(e) =>
            onChange({
              ...filter,
              recentActiveDays: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          options={[
            { value: '', label: t('recentlyActiveAny') },
            ...RECENT_ACTIVE_DAY_OPTIONS.map((days) => ({
              value: String(days),
              label: t('recentlyActiveDays', { days }),
            })),
          ]}
          disabled={disabled}
        />
      </Field>
    </Row>
  );
}

interface AudienceRecipientCountProps {
  readonly state: AudienceState;
  readonly sendVia: BroadcastSendVia;
  readonly onFilterChange: (f: AudienceFilterState) => void;
  readonly disabled: boolean;
}

// Live recipient count for the channel the platform composer sends on (the
// count applies the same recipient gate the dispatcher does). A country facet
// silently skips members with no known location, so the unknown count and
// the include-unknown toggle sit beside the total.
function AudienceRecipientCount({ state, sendVia, onFilterChange, disabled }: AudienceRecipientCountProps) {
  const t = useTranslations('Admin.broadcasts.audience');
  const countQ = useAdminBroadcastAudience(
    hasRecipientSelection(state) ? audienceSpecFromState(state) : null,
    sendVia,
  );
  if (!hasRecipientSelection(state)) return null;

  const country = state.kind === 'filter' ? state.filter.country : null;
  const unknownCountryCount = countQ.data?.unknownCountryCount;

  return (
    <Row gap="lg" align="center" wrap>
      {countQ.isError ? (
        <Body size="sm" color="destructive">
          {t('recipientCountError')}
        </Body>
      ) : countQ.data === undefined ? (
        <Caption>{t('recipientCountLoading')}</Caption>
      ) : (
        <Label>{t('recipientCount', { count: countQ.data.count })}</Label>
      )}
      {country !== null && unknownCountryCount !== undefined ? (
        <Checkbox
          checked={country.includeUnknown}
          onCheckedChange={(checked) =>
            onFilterChange({
              ...state.filter,
              country: { ...country, includeUnknown: checked === true },
            })
          }
          label={t('includeUnknownCountry', { count: unknownCountryCount })}
          disabled={disabled}
        />
      ) : null}
    </Row>
  );
}
